import { useRef, useState } from 'react';

import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';

import type { PostCsvPreviewState } from '../../hooks/campaign-create-shared';
import { CampaignPostImportPreview } from '../ui/post-section/campaign-post-import-preview';

type Props = {
  onApplyCsvImport: (preview: PostCsvPreviewState) => void;
  onDownloadTemplate: () => void;
  onPreviewCsvImport: (file: File) => Promise<PostCsvPreviewState>;
  remainingSlots: number;
};

export function CampaignPostCsvImportDialog({
  onApplyCsvImport,
  onDownloadTemplate,
  onPreviewCsvImport,
  remainingSlots,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<PostCsvPreviewState>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetDialog = () => {
    setPreview(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetDialog();
    }
  };

  const handleFileChange = async (file: File | undefined) => {
    if (!file) return;
    const nextPreview = await onPreviewCsvImport(file);
    setPreview(nextPreview);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirm = () => {
    if (!preview || preview.summary.added === 0) return;
    onApplyCsvImport(preview);
    handleOpenChange(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(true)}
        disabled={remainingSlots === 0}
      >
        Import CSV
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-[680px] gap-5">
          <DialogHeader>
            <DialogTitle>Import post URLs from CSV</DialogTitle>
            <DialogDescription>
              Review what will be added before applying it to this campaign
            </DialogDescription>
          </DialogHeader>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(event) => {
              void handleFileChange(event.target.files?.[0]);
            }}
          />

          <div className="flex flex-wrap items-center gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDownloadTemplate}
            >
              Download template
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={remainingSlots === 0}
            >
              Upload CSV
            </Button>
          </div>

          <CampaignPostImportPreview
            count={preview?.summary.added ?? 0}
            label="Post URLs to add:"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!preview || preview.summary.added === 0}>
              Add post URLs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
